import { HStack, Pressable, Text, VStack } from 'native-base';
import React, { ReactNode } from 'react';
import { Platform } from 'react-native';

import RadioIndicator from 'app/components/RadioIndicator';
import { Locale } from 'app/generated/hygraph';
import { useI18n } from 'app/providers/I18nProvider';
import InsiderServiceMobile from 'app/services/Insider';
import InsiderServiceWeb from 'app/services/InsiderWeb';
import Logger from 'app/services/Logger';
import { tID } from 'app/services/TestHelper';

export type LocaleSelectorProps = {
  children?: ReactNode;
};

const InsiderService = Platform.OS === 'web' ? InsiderServiceWeb : InsiderServiceMobile;

const LANGUAGE_OPTION_TEST_IDS: Record<Locale, string> = {
  [Locale.En]: 'LanguageOptionEnglish',
  [Locale.Fr]: 'LanguageOptionFrench',
  [Locale.De]: 'LanguageOptionGerman',
  [Locale.Es]: 'LanguageOptionSpanish',
  [Locale.It]: 'LanguageOptionItalian',
  [Locale.Ja]: 'LanguageOptionJapanese',
  [Locale.Pt]: 'LanguageOptionPortuguese',
  [Locale.Zh]: 'LanguageOptionChinese',
};

export default function LocaleRadio() {
  const { locale, locales, setLocale } = useI18n();

  const handleLocaleChange = ($locale: Locale) => {
    setLocale($locale);
    try {
      Promise.resolve(InsiderService.handleModifyUser({ locale: $locale })).catch((error) => {
        Logger.warn('[LocaleRadio] unable to update Insider language', { error });
      });
    } catch (error) {
      Logger.warn('[LocaleRadio] unable to update Insider language', { error });
    }
  };

  return (
    <VStack
      accessibilityLabel="Selected Language"
      accessibilityRole="radiogroup"
      testID={tID('LocaleSelector')}
    >
      {locales.map(([$locale, { label }]) => {
        const languageOptionTestID = tID(LANGUAGE_OPTION_TEST_IDS[$locale]);

        return (
          <Pressable
            key={$locale}
            accessibilityLabel={languageOptionTestID}
            accessibilityRole="radio"
            accessibilityState={{ checked: locale === $locale }}
            testID={languageOptionTestID}
            onPress={() => handleLocaleChange($locale)}
          >
            <HStack alignItems="center" space="2">
              <RadioIndicator isSelected={locale === $locale} />
              <Text px={3} py={3.5} testID={tID(`locale-txt-${$locale}`)}>
                {label}
              </Text>
            </HStack>
          </Pressable>
        );
      })}
    </VStack>
  );
}
